<template>
  <div class="related-gl-codes-for-order flex flex-column flex-gap-1">
    <div
      class="related-gl-codes-for-order-header flex flex-gap-1 align-items-center"
    >
      <h3 class="section-subheader m-0">
        {{ t('orders.orderNumParam', { param: internalValue.id }) }}
      </h3>
      <LoadingIndicator :loading="loading || saving" />
    </div>
    <div class="flex flex-gap-1 flex-wrap">
      <p v-if="!sortedGLCodes.length">
        {{ t('common.noParam', { param: t('glCodes.glCodes', 2) }) }}
      </p>
      <GLCodeInlineEdit
        v-for="(glCode, index) in sortedGLCodes"
        :key="glCode.code"
        :model-value="glCode"
        :editable="editable"
        @remove="onRemove"
        @update:model-value="onUpdate(index, $event)"
      />
    </div>

    <div v-if="newGLCode" class="related-gl-code-section-new flex">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId }"
        :input-id="`related-gl-code-section-${internalValue.id}-new-code`"
        :label="t('glCodes.glCodes')"
        class="w-6"
      >
        <GLCodeAutocomplete
          :id="inputId"
          v-model="newGLCode"
          v-bind="ariaProps"
          :class="inputClasses"
          :gl-codes="availableGLCodeOptions"
          :placeholder="t('glCodes.glCodes')"
          @keydown.enter="onSubmit"
          @keydown.esc="onCancel"
        />
      </InputWrapper>
    </div>

    <div
      class="related-gl-code-section-buttons flex flex-gap-1 justify-content-end"
    >
      <RygenButton
        v-if="newGLCode"
        mode="tertiary"
        :label="t('common.remove')"
        icon="pi pi-times"
        icon-pos="left"
        @click="onCancel"
      />
      <RygenButton
        v-if="newGLCode"
        mode="tertiary"
        :disabled="!newGLCode.code"
        :label="t('common.save')"
        icon="pi pi-check"
        icon-pos="left"
        @click="onSubmit"
      />
      <RygenButton
        v-else-if="editable"
        mode="tertiary"
        class="add-button"
        :label="
          t('common.addParamToParam2', {
            param: t('glCodes.glCodes'),
            param2: t('orders.orderNumParam', { param: internalValue.id }),
          })
        "
        @click="onAdd"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';
import { GLCode } from '@rygen/rygen-ui/model';
import { alphabeticComparator, notNull } from '@rygen/rygen-ui/util';

import { Order } from '@/model';
import { useGLCodeStore } from '@/stores/GLCode';
import { glCodeComparator } from '@/util';

import GLCodeAutocomplete from '@/components/glCode/GLCodeAutocomplete.vue';
import GLCodeInlineEdit from '@/components/glCode/GLCodeInlineEdit.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';

const props = defineProps({
  modelValue: {
    type: Order,
    default: () => new Order(),
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  saving: {
    type: Boolean,
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, loading, saving, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: Order): void;
}>();

const { availableGLCodes, loadGLCodes } = useGLCodeStore();
const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<Order>) => new Order(opts),
});

const newGLCode: Ref<GLCode | null> = ref(null);
const sortByName = alphabeticComparator({ isCaseSensitive: false });

const sortedGLCodes: Ref<GLCode[]> = computed(() => {
  return internalValue.value.glCodes?.toSorted(glCodeComparator) ?? [];
});

// Filter out already used types
const availableGLCodeOptions = computed(() => {
  return (
    availableGLCodes.value.data
      ?.filter((clientType) => {
        const existingGLCode = internalValue.value.glCodes?.find(
          (g) => g.code && g.code === clientType.code,
        );
        return !existingGLCode;
      })
      ?.filter(notNull)
      ?.sort((a: GLCode, b: GLCode) => sortByName(a.code, b.code)) ?? []
  );
});

const onCancel = () => {
  newGLCode.value = null;
};

const onAdd = () => {
  newGLCode.value = new GLCode({ code: '' });
};

const onSubmit = () => {
  if (!newGLCode.value?.code || !internalValue.value.glCodes) {
    return;
  }

  internalValue.value.glCodes.push(newGLCode.value);
  newGLCode.value = null;
};

const onRemove = (glCode: GLCode) => {
  if (!internalValue.value.glCodes) {
    return;
  }

  const index = internalValue.value.glCodes.findIndex(
    (g) => g.code === glCode.code,
  );
  if (index === -1) {
    return;
  }

  internalValue.value.glCodes.splice(index, 1);
};

const onUpdate = (index: number, glCode: GLCode) => {
  if (!internalValue.value.glCodes) {
    return;
  }

  // `index` is the index into `sortedGLCodes`; convert it to an index into
  // `internalValue.glCodes`
  const originalGLCode = sortedGLCodes.value[index];
  const originalIndex = internalValue.value.glCodes.findIndex(
    (g) => g.code === originalGLCode.code,
  );

  internalValue.value.glCodes.splice(originalIndex, 1, glCode);
};

onMounted(() => {
  if (editable.value) {
    loadGLCodes();
  }
});
</script>
